import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { merge, set } from 'lodash';
import queryString from 'query-string';
import AuthLoader from '@lendi-ui/auth-loader';

import { FIELD_PATHS, FIELD_VALUES, GP_BP_FORM_PREFILL, URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { FunnelConfig } from '@funnels/customer-view/src/configs';
import generateLead from '@funnels/customer-view/src/utils/generateLead';
import { FormValuesFunnelOne, PropertyAsset, ReduxStoreData, UseParams } from '@funnels/types';
import { SubmittableForm } from '@funnels/ui-components';
import {
  getLandingLocation,
  initialFormValues,
  removeUnlinkedCompaniesAndIncomes,
  sessionStorageUtil,
} from '@funnels/utils';

function mapState(state: ReduxStoreData) {
  const loading =
    state.applications.loading +
    state.applicants.loading +
    state.assets.loading +
    state.companies.loading +
    state.liabilities.loading +
    state.employments.loading +
    state.expenses.loading +
    state.incomes.loading;
  const data = {
    applications: state.applications.data,
    applicants: state.applicants.data,
    assets: state.assets.data,
    companies: state.companies.data,
    employments: state.employments.data,
    expenses: state.expenses.data,
    incomes: state.incomes.data,
    liabilities: state.liabilities.data,
  };

  return {
    loading,
    data,
  };
}

interface FunnelOneFormProps {
  config: FunnelConfig;
  isStartup?: boolean;
  purpose?: string;
}

function FunnelOneForm(props: FunnelOneFormProps) {
  const history = useHistory();
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = useParams<UseParams>();
  const bpFormPrefill = sessionStorageUtil.get(GP_BP_FORM_PREFILL);
  const { loading, data } = useSelector(mapState);

  const { purpose } = props;
  const primaryPurpose = purpose !== FIELD_VALUES.REFINANCE ? FIELD_VALUES.NEW_PURCHASE : FIELD_VALUES.REFINANCE;
  // If landing BP from property hub pages with prefilled questions, we merge prefilled data during init
  const initialValues = useMemo(() => {
    if (!applicationId) {
      let initialFunnelOneFormValue;
      switch (purpose) {
        case FIELD_VALUES.NEW_PURCHASE:
          initialFunnelOneFormValue = initialFormValues.INITIAL_FUNNEL_NEW_PURCHASE_FORM_VALUE();
          break;
        case FIELD_VALUES.REFINANCE:
          initialFunnelOneFormValue = initialFormValues.INITIAL_FUNNEL_REFINANCING_FORM_VALUE();
          break;
        case FIELD_VALUES.BORROWING_POWER:
        default:
          initialFunnelOneFormValue = initialFormValues.INITIAL_FUNNEL_BORROWING_POWER_VALUE();
          if (bpFormPrefill) {
            if (bpFormPrefill.numberOfApplicants === 2) {
              initialFunnelOneFormValue.applicants.push(initialFormValues.INITIAL_APPLICANT());
            }
            if (bpFormPrefill.deposit) {
              initialFunnelOneFormValue.applications[0].preliminary.contributions[0].amount = bpFormPrefill.deposit;
            }
            if (bpFormPrefill.purchaseType && initialFunnelOneFormValue.assets) {
              (initialFunnelOneFormValue.assets[0] as PropertyAsset).primaryPurpose = bpFormPrefill.purchaseType;
            }
          }

          break;
      }
      if (purpose) {
        set(initialFunnelOneFormValue, FIELD_PATHS.APPLICATION_PRIMARY_PURPOSE(0), primaryPurpose);
      }
      return initialFunnelOneFormValue;
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, purpose]);

  function handleSubmit(nextQuestion: any, values: FormValuesFunnelOne, urlParams: UseParams = {}) {
    if (nextQuestion.hardRedirect === true) {
      redirect(nextQuestion.url, values, urlParams);
    } else {
      goToNextQuestion(nextQuestion.url, urlParams);
    }
  }

  const goToNextQuestion = (url: string, urlParams: UseParams) => {
    const newURL = generatePath(url, urlParams);
    history.push(newURL);
  };

  const redirect = (url: string, values: FormValuesFunnelOne, urlParams: UseParams) => {
    // before submitting the form, we will clean up "orphan" companies and associated incomes
    removeUnlinkedCompaniesAndIncomes(values);

    sessionStorageUtil.set('funnel_one_form_values', values);
    const newURL = generatePath(url, urlParams);

    // Lead generation and sending
    generateLead(values);

    const landingParams = getLandingLocation ? queryString.parse(getLandingLocation.search) : {};
    // Skip flag only used for funnel-one pages
    // In funnel one, redirect will be used for redirecting user to landing page, ctp and funnel two
    // For example, if you lending at "/home-loans/basics/application/applicationId/refinance?skip=true"
    // The page you want to redirect ---- landing page, ctp page and funnel-two page, doesn't need the "skip" flag
    if (landingParams['skip']) {
      delete landingParams['skip'];
    }
    const parsedURL = queryString.parseUrl(newURL);
    const urlWithQueryParams = `${parsedURL.url}?${queryString.stringify(merge(landingParams, parsedURL.query))}`;

    window.location.assign(urlWithQueryParams);
  };

  if (loading > 0) {
    return <AuthLoader />;
  }

  return (
    <SubmittableForm
      {...props}
      initialValues={initialValues}
      applicantIndex={0}
      assetIndex={0}
      liabilityIndex={0}
      onSubmit={handleSubmit}
      funnelType="funnelOne"
    />
  );
}

export default FunnelOneForm;
